import { Grid2 } from "@mui/material";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import styles from "./OrgDashboardStyle.module.scss";
import Licenses from "../../../../AdminComponents/Licenses/Licenses";
import LicenseUsage from "../../../../AdminComponents/LicenseUsage/LicenseUsage";
import UserActivity from "../../../../AdminComponents/UserActivity/UserActivity";
import TopSites from "../../../../AdminComponents/TopSites/TopSites";
import OrganizationName from "../../../../AdminComponents/OrganizationName/OrganizationName";
import { useMakeApiCalls } from "../../../../../../Hooks/useMakeApiCalls";
import { Actions } from "../../../../../../redux/constants/actionTypes";
import { getStore } from "../../../../../../Utils/storeUtils";
import { useSelector } from "react-redux";
import { getExpiryString, getUsage } from "../../../../../../Utils/utils";
import { getActivityMessage } from "../../../../../../Utils/activityConstants";
import { planIcons } from "../../../../../../Utils/planIcons";
import PageLoading from "../../../../../../GlobalComponents/PageLoading/PageLoading";
import WelcomeDashboard from "../../../../../../GlobalComponents/WelcomeDashboard/WelcomeDashboard";
import { ReactComponent as PersonalIcon } from "../../../../../../GlobalAssets/svg/personalIcon.svg";
import { ReactComponent as ClientIcon } from "../../../../../../GlobalAssets/svg/clientIcon.svg";
import { ReactComponent as ParentIcon } from "../../../../../../GlobalAssets/svg/parentIcon.svg";
import { formatFileSizeCaps } from "../../../../../../Utils/roconConstants";
import PlanExpiredCard from "../../../../../../GlobalComponents/PlanExpiredCard/PlanExpiredCard";

function OrganizationDashboard() {
  const navigate = useNavigate();
  const [selectedTimeRange, setSelectedTimeRange] = useState("Month");
  const [selectedUsageRange, setSelectedUsageRange] = useState("Month");
  const [isLoading, setIsLoading] = useState(true);
  const params = useParams();
  const orgId = parseInt(params.orgId, 10);
  const intervalRef = useRef(null);
  // to get all plans
  const allPlans = useSelector((state) => state.plansInfo.allPlans);
  useEffect(() => {
    if (!allPlans) {
      getStore().dispatch({ type: Actions.API_ALL_PLANS });
    }
  }, [allPlans]);

  useMakeApiCalls(() => {
    if (orgId > 0) {
      getStore().dispatch({ type: Actions.API_ORG_DASHBOARD });
    }
    getStore().dispatch({ type: Actions.API_USER_DETAILS });
    getStore().dispatch({
      type: Actions.API_TOP_SITES,
      data: { orgId: orgId },
    });
  });

  const organizationsInfo = useSelector((state) => state.organizationsInfo);
  const selectedOrg = organizationsInfo?.selectedOrg;
  const parentOrgName = selectedOrg?.parentOrgName;

  const sitesInfo = useSelector((state) => state.sitesInfo);
  const selectedPlan = useSelector(
    (state) => state.plansInfo?.selectedPlan?.plan,
  );

  const specificPlan = useSelector((state) => state.plansInfo?.specificPlan);
  const plansInfo = specificPlan?.specificPlans;
  const dedicatedHosting = specificPlan?.isDedicated;
  // const orgId = getOrgIdByOrgname(orgName, organizationsInfo);

  const imagePath = useSelector(
    (state) => state.organizationsInfo?.selectedOrg?.imagePath,
  );

  // to get org activities
  useEffect(() => {
    if (orgId !== -1) {
      getStore().dispatch({
        type: Actions.API_GET_ORG_ACTIVITIES,
        data: {
          type: selectedTimeRange,
          orgId: orgId,
          startDate: null,
          endDate: null,
        },
      });
    } else if (orgId === -1) {
      getStore().dispatch({
        type: Actions.API_PERSONAL_USER_ACTIVITIES,
        data: {
          type: selectedTimeRange,
          startDate: null,
          endDate: null,
        },
      });
    }
  }, [selectedTimeRange, orgId]);

  useEffect(() => {
    if (orgId) {
      getStore().dispatch({
        type: Actions.API_GET_TIMELINEGRAPH_DETAILS,
        data: {
          type: selectedUsageRange,
          orgId: orgId,
          startDate: null,
          endDate: null,
        },
      });
    } else if (orgId === -1) {
      getStore().dispatch({
        type: Actions.API_GET_TIMELINEGRAPH_DETAILS,
        data: {
          type: selectedUsageRange,
          orgId: -1,
          startDate: null,
          endDate: null,
        },
      });
    }
  }, [selectedUsageRange, orgId]);

  useEffect(() => {
    const fetchPlan = () => {
      getStore().dispatch({
        type: Actions.API_GET_SEPCIFIC_PLAN,
        data: { orgId },
        meta: {
          onSuccess: () => setIsLoading(false),
          onError: () => setIsLoading(false),
        },
      });
      if (orgId > 0) {
        getStore().dispatch({
          type: Actions.API_GET_SELECTED_ORG_FULL_INFO,
          orgId: orgId,
        });
      }
    };

    setIsLoading(true);
    fetchPlan();

    if (selectedPlan === true) {
      intervalRef.current = setInterval(fetchPlan, 5000);
    }

    return () => clearInterval(intervalRef.current);
  }, [orgId, selectedPlan]);

  useEffect(() => {
    if (plansInfo?.length > 0) {
      clearInterval(intervalRef.current);
    }
  }, [plansInfo?.length, parentOrgName]);

  // useEffect(() => {
  //   getStore().dispatch({
  //     type: Actions.API_ALL_SITES,
  //     data: { orgId: orgId ? orgId : -1 },
  //     meta: {
  //       onSuccess: () => {
  //         setIsLoading(false);
  //       },
  //       onError: () => {
  //         setIsLoading(false);
  //       },
  //     },
  //   });
  // }, [orgId]);

  const graphData = organizationsInfo?.timelineDetails || [];
  const orgActivity = organizationsInfo.selectedOrgActivity?.actions || [];
  const indvActivity = organizationsInfo.userActivities?.actions || [];
  const selectedSites = sitesInfo?.topSites?.data;

  const topVisitedSites = useMemo(() => {
    return (
      selectedSites &&
      selectedSites
        ?.filter((site) => site.status !== "provision")
        .map((site) => {
          const visitData = site.featuresUsageRes?.find(
            (feature) => feature.type === "VISIT",
          );
          return {
            ...site,
            visitCount: parseInt(visitData?.used || "0", 10),
          };
        })
        .sort((a, b) => b.visitCount - a.visitCount)
        .slice(0, 5)
    );
  }, [selectedSites]);

  const userDetails = useSelector((state) => state.users?.userDetails);

  const plan = plansInfo?.find((item) => item.basePlan === true) || {};
  const cancel = plan?.cancel;
  const daysAfterExpiry = plan?.daysAfterExpiry;

  const additionalSites = useSelector((state) => {
    const specificPlans = state.plansInfo?.specificPlan?.specificPlans || [];
    return (
      specificPlans.find((plan) => plan.licenseType === "Additional Site") || []
    );
  });

  const planFeatures = plan?.featureDetails;
  const planName = plan?.licenseType;
  const planExpiry = plan?.expirationDate;
  const planCreation = plan?.creationDate;
  const planExpirDay = getExpiryString(planExpiry);
  const PlanIcon = planName && planIcons[planName];

  const siteUsage = `${getUsage("NUM_SITES", planFeatures)?.used || 0} out of ${
    (parseInt(getUsage("NUM_SITES", planFeatures)?.limitOrAvailablity, 10) ||
      0) + (parseInt(additionalSites?.quantity, 10) || 0)
  }`;

  const storageUsage = `${formatFileSizeCaps(
    getUsage("STORAGE", planFeatures)?.used > 0
      ? parseInt(getUsage("STORAGE", planFeatures)?.used)
      : 0,
  )} out of ${getUsage("STORAGE", planFeatures)?.limitOrAvailablity} GB`;

  // for circular license bae
  const sites = getUsage("NUM_SITES", planFeatures)?.used;
  const clientSites = organizationsInfo?.selectedOrg?.stats?.numOfWebSites;
  const sitesLimit = getUsage("NUM_SITES", planFeatures)?.limitOrAvailablity;
  const storage =
    getUsage("STORAGE", planFeatures)?.used > 0
      ? getUsage("STORAGE", planFeatures)?.used
      : 0;
  const storageLimit = getUsage("STORAGE", planFeatures)?.limitOrAvailablity;
  const dedicatedStorage = useSelector(
    (state) => state.sitesInfo?.dedicatedStorage,
  );

  const handleSiteClick = (site) => {
    navigate(`/account/${orgId}/${site.siteId}/site-details`);
  };
  // const activities = orgId
  //   ? orgActivity.map((activity) => {
  //       const parsedDate = new Date(activity.createdTs);
  //       return {
  //         actionType: activity.actionType,
  //         message: getActivityMessage(activity),
  //         time: parsedDate.toLocaleString(),
  //         name: activity.userName,
  //       };
  //     })
  //   : indvActivity.map((activity) => {
  //       const parsedDate = new Date(activity.createdTs);
  //       return {
  //         actionType: activity.actionType,
  //         message: getActivityMessage(activity),
  //         time: parsedDate.toLocaleString(),
  //         name: activity.userName,
  //       };
  //     });

  return (
    <>
      {userDetails?.userName && !isLoading && allPlans ? (
        <>
          {plansInfo?.length === 0 ? (
            <WelcomeDashboard />
          ) : (
            <>
              <Grid2 className={styles.Container}>
                <Grid2 className={styles.header}>Dashboard</Grid2>
                <Grid2 className={styles.Organization}>
                  <OrganizationName
                    image={
                      imagePath ? (
                        <img
                          src={imagePath}
                          alt="orgImage"
                          style={{
                            width: "110px",
                            height: "110px",
                            borderRadius: "4px",
                          }}
                        />
                      ) : orgId === -1 ? (
                        <PersonalIcon className={styles.imgContainer} />
                      ) : parentOrgName === null ? (
                        <ParentIcon className={styles.imgContainer} />
                      ) : (
                        <ClientIcon className={styles.imgContainer} />
                      )
                    }
                    name={
                      orgId === -1 ? userDetails?.userName : selectedOrg?.name
                    }
                    owner={
                      orgId === -1
                        ? userDetails?.emailId
                        : selectedOrg?.ownerEmailId
                    }
                  />
                  {orgId > 0 ? (
                    <>
                      {parentOrgName === null ? (
                        <>
                          {plan ? (
                            <Licenses
                              image={PlanIcon}
                              planName={planName}
                              expiry={planExpirDay}
                              siteUsage={siteUsage}
                              storageUsage={storageUsage}
                              planExpiry={`${planExpirDay} left`}
                              sites={sites}
                              sitesLimit={sitesLimit}
                              storage={storage}
                              storageLimit={storageLimit}
                              plan={planCreation}
                              planLimit={planExpiry}
                              singleSite={plan?.isSingleSite}
                              expired={plan?.daysAfterExpiry}
                              dedicatedHosting={dedicatedHosting}
                              basePlan={plan}
                            />
                          ) : (
                            <Licenses noPlan={true} />
                          )}
                        </>
                      ) : (
                        <Licenses
                          parentName={parentOrgName}
                          selectedOrg={selectedOrg}
                          sites={clientSites}
                        />
                      )}
                    </>
                  ) : (
                    <>
                      {plan !== undefined ? (
                        <Licenses
                          image={PlanIcon}
                          planName={planName}
                          singleSite={plan?.isSingleSite}
                          expiry={planExpirDay}
                          siteUsage={siteUsage}
                          storageUsage={storageUsage}
                          planExpiry={`${planExpirDay} left`}
                          sites={sites}
                          sitesLimit={sitesLimit}
                          storage={storage}
                          storageLimit={storageLimit}
                          plan={planCreation}
                          planLimit={planExpiry}
                          dedicatedHosting={dedicatedHosting}
                          basePlan={plan}
                        />
                      ) : (
                        <Licenses noPlan={true} />
                      )}
                    </>
                  )}
                </Grid2>

                <LicenseUsage
                  bandwidthGraphResponse={graphData?.bandwidthGraphResponse}
                  storageGraphResponse={graphData?.storageGraphResponse}
                  visitsGraphResponse={graphData?.visitsGraphResponse}
                  maxBandwidth={
                    getUsage("STORAGE", planFeatures)?.limitOrAvailablity
                  }
                  setSelectedUsageRange={setSelectedUsageRange}
                />

                <Grid2 className={styles.Activity}>
                  <TopSites
                    sites={topVisitedSites}
                    onClick={(site) => handleSiteClick(site)}
                    orgId={orgId}
                    navigate={navigate}
                  />
                  {orgId !== -1 && (
                    <UserActivity
                      activities={orgActivity.map((activity) => {
                        const parsedDate = new Date(activity.createdTs);
                        return {
                          actionType: activity.actionType,
                          payload: activity.payload,
                          message: getActivityMessage(activity, allPlans),
                          time: parsedDate.toLocaleString(),
                          name: activity.userName,
                        };
                      })}
                      setSelectedTimeRange={setSelectedTimeRange}
                    />
                  )}

                  {orgId === -1 && (
                    <UserActivity
                      activities={indvActivity.map((activity) => {
                        const parsedDate = new Date(activity.createdTs);
                        return {
                          actionType: activity.actionType,
                          payload: activity.payload,
                          message: getActivityMessage(activity, allPlans),
                          time: parsedDate.toLocaleString(),
                          name: activity.userName,
                        };
                      })}
                      setSelectedTimeRange={setSelectedTimeRange}
                    />
                  )}
                </Grid2>

                {((cancel === true && daysAfterExpiry >= 2) ||
                  daysAfterExpiry >= 2) && (
                  <PlanExpiredCard org={selectedOrg} plan={plan} />
                )}

                <Outlet />
              </Grid2>
            </>
          )}
        </>
      ) : (
        <PageLoading />
      )}
    </>
  );
}

export default OrganizationDashboard;
